import { NgClass } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PasLayoutSidebarHolderComponent, PasSidebarHandleService } from '@porsche-aftersales/frontend-toolbox';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';

import { AfterSalesStore } from '../../../services/after-sales/after-sales-store.service';
import { AGGridExcelService } from '../../../services/ag-grid/excel-service';
import { AGGridFormatterService } from '../../../services/ag-grid/formatter-service';
import { NotificationServiceWrapper } from '../../../services/notification-service-wrapper/notification-service-wrapper';
import { UserService } from '../../../services/user-service/user.service';
import { AG_DATE_COL_CONFIG } from '../../../shared/ag-grid/ag-grid-functions';
import { AgGridService } from '../../../shared/ag-grid/ag-grid.service';
import { TableViewAdministrationState } from '../../../sub-component/table-view-administration/common/enum/table-view-administration-state.enum';
import { TvaSessionSidebarComponent } from '../../../sub-component/table-view-administration/tva-session/component-sidebar/tva-session-sidebar.component';
import { TvaSessionService } from '../../../sub-component/table-view-administration/tva-session/tva-session.service';
import { AfterSalesTabsetComponent } from '../after-sales-tabset.component';
import { AfterSalesModel } from '../model/after-sales-model';
import { AfterSalesButtonCellRenderer } from '../renderer/after-sales-button-cell-renderer.component';

@Component({
  selector: 'app-after-sales',
  styleUrls: ['./after-sales.component.scss'],
  templateUrl: './after-sales.component.html',
  imports: [AgGridAngular, PasLayoutSidebarHolderComponent, NgClass, TranslatePipe, TvaSessionSidebarComponent],
})
export class AfterSalesComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly sidebarHandleService = inject(PasSidebarHandleService);
  private readonly agGridService = inject(AgGridService);
  private readonly agGridFormatterService = inject(AGGridFormatterService);
  private readonly agGridExcelService = inject(AGGridExcelService);
  private readonly afterSalesStore = inject(AfterSalesStore);
  private readonly userService = inject(UserService);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly tvaSessionService = inject(TvaSessionService);
  private readonly afterSalesTabset = inject(AfterSalesTabsetComponent, { host: true });

  private gridApi: any;

  public columnDefs: any;
  public rowData: any;
  public defaultColDef: any;
  public excelStyles: any;
  public multiSortKey: any;
  public frameworkComponents: any;
  public isCreateExcelAfterSalesEnabled = true;
  public tabList: AfterSalesModel[];
  public tooltipShowDelay = 0;

  public gridOptions: GridOptions;
  public aftersalesOverviewData: any[] = [];

  public constructor() {
    this.frameworkComponents = {
      afterSalesButtonDetailsRenderer: AfterSalesButtonCellRenderer,
    };
  }

  public async ngOnInit(): Promise<void> {
    this.gridOptions = {
      rowHeight: 35,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      getLocaleText: this.agGridService.translate(this.userService, this.translateService),
      overlayComponentParams: {
        noMatchingRows: { overlayText: this.translateService.instant('noMatchingRows') },
      },
    };

    this.defaultColDef = {
      resizable: true,
      sortable: true,
      unSortIcon: true,
      floatingFilter: true,
    };

    this.translateService.get('AFTER_SALES_OVERVIEW_TAB').subscribe(() => {
      this.columnDefs = [
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_REPORT_NO'),
          headerTooltip: this.translateService.instant('AFTER_SALES_TABLE_TOOLTIP_REPORT_NO'),
          field: 'reportId',
          minWidth: 75,
          filter: 'agTextColumnFilter',
          comparator: (valueA: string, valueB: string) => {
            valueA = this.pad(valueA, 10);
            valueB = this.pad(valueB, 10);
            if (valueA == valueB) return 0;
            return valueA > valueB ? 1 : -1;
          },
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_SUPPLIER_ID'),
          headerTooltip: this.translateService.instant('AFTER_SALES_TABLE_TOOLTIP_SUPPLIER_ID'),
          field: 'supplierNo',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_SUPPLIER'),
          headerTooltip: this.translateService.instant('AFTER_SALES_TABLE_TOOLTIP_SUPPLIER'),
          field: 'supplierName',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_ERROR_CODE'),
          headerTooltip: this.translateService.instant('AFTER_SALES_TABLE_TOOLTIP_ERROR_CODE'),
          field: 'errorCode',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_DESCRIPTION'),
          headerTooltip: this.translateService.instant('AFTER_SALES_TABLE_TOOLTIP_DESCRIPTION'),
          field: 'errorDescription',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_ERROR_CLASS'),
          headerTooltip: this.translateService.instant('AFTER_SALES_TABLE_TOOLTIP_ERROR_CLASS'),
          field: 'errorClass',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_CREATE_DATE'),
          headerTooltip: this.translateService.instant('AFTER_SALES_TABLE_TOOLTIP_CREATE_DATE'),
          field: 'reportCreationDate',
          valueFormatter: this.agGridFormatterService.dateFormatter,
          ...AG_DATE_COL_CONFIG,
          cellStyle: { textAlign: 'right' },
          minWidth: 155,
          cellClass: 'dateFormat',
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_RECOURSE_RELEVANT'),
          headerTooltip: this.translateService.instant('AFTER_SALES_TABLE_TOOLTIP_RECOURSE_RELEVANT'),
          valueGetter: this.agGridService.getAuditReportYesAndNoTranslation(this.translateService),
          filter: 'agTextColumnFilter',
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_RECOURSE_COST_RATE'),
          headerTooltip: this.translateService.instant('AFTER_SALES_TABLE_TOOLTIP_RECOURSE_COST_RATE'),
          field: 'recourseCostRate',
          valueFormatter: this.agGridFormatterService.currencyFormatterDefault(this.userService),
          filter: 'agTextColumnFilter',
          cellStyle: { textAlign: 'right' },
          cellClass: 'currencyFormat',
          filterParams: {
            textFormatter: val => {
              if (val == null) {
                return null;
              }
              return val.toString().replace(',', '.');
            },
          },
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_ADDITIONAL_COST'),
          headerTooltip: this.translateService.instant('AFTER_SALES_TABLE_TOOLTIP_ADDITIONAL_COST'),
          field: 'additionalCostSum',
          valueFormatter: this.agGridFormatterService.currencyFormatterDefault(this.userService),
          filter: 'agTextColumnFilter',
          cellStyle: { textAlign: 'right' },
          cellClass: 'currencyFormat',
          filterParams: {
            textFormatter: val => {
              if (val == null) {
                return null;
              }
              return val.toString();
            },
          },
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_TOTAL_COST'),
          headerTooltip: this.translateService.instant('AFTER_SALES_TABLE_TOOLTIP_TOTAL_COST'),
          field: 'totalCost',
          valueFormatter: this.agGridFormatterService.currencyFormatterDefault(this.userService),
          filter: 'agTextColumnFilter',
          cellStyle: { textAlign: 'right' },
          cellClass: 'currencyFormat',
          filterParams: {
            textFormatter: val => {
              if (val == null) {
                return null;
              }
              return val.toString().replace(',', '.');
            },
          },
        },
        {
          headerName: this.translateService.instant('AFTER_SALES_TABLE_REPORT_STATE'),
          headerTooltip: this.translateService.instant('AFTER_SALES_TABLE_TOOLTIP_REPORT_STATE'),
          valueGetter: this.agGridService.translateAuditReportState(this.translateService),
          filter: 'agSetColumnFilter',
          filterParams: {
            suppressSelectAll: false,
            suppressMiniFilter: true,
          },
        },
        {
          colId: 'buttonColumn',
          headerName: '',
          headerClass: ['ag-header-last-cell'],
          minWidth: 50,
          maxWidth: 50,
          filter: false,
          cellRenderer: 'afterSalesButtonDetailsRenderer',
          cellRendererParams: {
            onClick: this.onDetailsBtn.bind(this),
          },
          field: 'value',
          menuTabs: [],
          unSortIcon: false,
        },
      ];
    });

    this.excelStyles = this.agGridExcelService.getExcelStyles(this.translateService);
    this.multiSortKey = 'ctrl';
  }

  private pad(text: string, size: number): string {
    while (text.length < size) text = `0${text}`;
    return text;
  }

  public enableSidebar(): void {
    if (this.afterSalesTabset.userHasAnyRight()) {
      this.sidebarHandleService.enableSidebarForView();
    }
  }

  public async onGridReady(event: GridReadyEvent): Promise<void> {
    this.enableSidebar();
    this.gridApi = event.api;
    this.gridApi.updateGridOptions({
      headerHeight: 32,
    });
    event.api.applyColumnState({
      state: [{ colId: 'reportId', sort: 'asc' }],
    });
    this.aftersalesOverviewData = await this.afterSalesStore.getAfterSalesTabData();
    event.api.setGridOption('rowData', this.aftersalesOverviewData);
    this.tvaSessionService.init(TableViewAdministrationState.AfterSales, false, this.gridOptions, null, this.gridApi);
    this.isCreateExcelAfterSalesEnabled = this.afterSalesTabset.userHasAnyRight();
  }

  public onBtnCreateExcelAfterSales(): void {
    try {
      this.gridApi.exportDataAsExcel(
        this.agGridExcelService.getExcelFrame(
          this.translateService.instant('AFTER_SALES_OVERVIEW_TAB'),
          this.translateService.instant('EXCEL_EXPORT_AFTER_SALES_OVERVIEW'),
          this.gridOptions,
        ),
      );
    } catch {
      const title: string = this.translateService.instant('EXCEL_EXPORT_ERROR_TITLE');
      const content: string = this.translateService.instant('EXCEL_EXPORT_ERROR_CONTENT');
      this.notificationServiceWrapper.addErrorNotification(title, content);
    }
  }

  public getContextMenuItems(): any[] {
    return [];
  }

  private onDetailsBtn(event: any): void {
    this.afterSalesTabset.navigateToDetails(event.rowData.reportId);
  }

  public isDetailsTabDisabled(): boolean {
    return this.afterSalesTabset.detailsTabDisabled;
  }
}
